import { triggerTemplate, validateActionBased, type ActionBasedIssue, type TriggerSpec } from '../../../campaigns/trigger/actionBased.ts';
import { EVERYONE_ELSE } from './ports.ts';

/**
 * Action Paths settings as the editor reads and writes them (BB-117): the shape of `ActionPathsConfig` in `@bb/domain`
 * (`steps/action-paths.ts`). Triggers are the BB-100 `TriggerSpec`, checked with the same contract as campaign triggers.
 */
export interface ActionPathsGroup {
  id: string;
  name: string;
  triggers: TriggerSpec[];
  exitAfter?: boolean;
}

export interface ActionPathsConfig {
  windowSeconds: number;
  ranking: boolean;
  groups: ActionPathsGroup[];
  everyoneElse: { exitAfter?: boolean };
}

export const ACTION_PATHS_MAX_GROUPS = 8;
/** Braze: up to 31 days. */
export const ACTION_PATHS_MAX_WINDOW_SECONDS = 31 * 86_400;
export const ACTION_PATHS_MAX_TRIGGERS = 10;
const GROUP_ID = /^[a-z0-9][a-z0-9_-]{0,39}$/;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** The stored settings with the server's defaults filled in and unknown keys left out. */
export function readActionPaths(raw: unknown): ActionPathsConfig {
  const c = isObject(raw) ? raw : {};
  const groups = (Array.isArray(c.groups) ? c.groups : []).filter(isObject).map(
    (g): ActionPathsGroup => ({
      id: typeof g.id === 'string' ? g.id : '',
      name: typeof g.name === 'string' ? g.name : '',
      triggers: Array.isArray(g.triggers) ? (g.triggers as TriggerSpec[]) : [],
      ...(g.exitAfter === true ? { exitAfter: true } : {}),
    }),
  );
  const everyoneElse = isObject(c.everyoneElse) && c.everyoneElse.exitAfter === true ? { exitAfter: true } : {};
  return { windowSeconds: typeof c.windowSeconds === 'number' ? c.windowSeconds : 86_400, ranking: c.ranking === true, groups, everyoneElse };
}

/** A group id (its port) not used yet: `group_1`, `group_2`, … */
export function nextGroupId(groups: readonly ActionPathsGroup[]): string {
  for (let n = groups.length + 1; ; n++) if (!groups.some((g) => g.id === `group_${n}`)) return `group_${n}`;
}

export const newGroup = (groups: readonly ActionPathsGroup[]): ActionPathsGroup => ({ id: nextGroupId(groups), name: `Group ${groups.length + 1}`, triggers: [triggerTemplate('custom_event')] });

/** `exitAfter` set, or left out (the stored config never holds `false`). */
export const withExit = <T extends { exitAfter?: boolean }>(item: T, exit: boolean): T => {
  const { exitAfter: _old, ...rest } = item;
  return (exit ? { ...rest, exitAfter: true } : rest) as T;
};

export interface ActionPathsIssues {
  /** `windowSeconds`, `ranking`, `groups`. */
  fields: Record<string, string>;
  /** Per group: its name (`name`), its id and the issues of its triggers (paths `['triggers', i, …]`, as `TriggerList` reads them). */
  groups: Array<{ name?: string; id?: string; triggers: ActionBasedIssue[] }>;
}

/** The schema's rules as messages per field. */
export function actionPathsIssues(c: ActionPathsConfig): ActionPathsIssues {
  const fields: Record<string, string> = {};
  if (!Number.isInteger(c.windowSeconds) || c.windowSeconds < 1) fields.windowSeconds = 'The window is at least 1 second.';
  else if (c.windowSeconds > ACTION_PATHS_MAX_WINDOW_SECONDS) fields.windowSeconds = 'The window is at most 31 days.';
  if (c.ranking) fields.ranking = 'Ranking is not available yet: turn it off.';
  if (c.groups.length === 0) fields.groups = 'Add at least one group.';
  if (c.groups.length > ACTION_PATHS_MAX_GROUPS) fields.groups = `At most ${ACTION_PATHS_MAX_GROUPS} groups.`;
  const groups = c.groups.map((g) => {
    const out: ActionPathsIssues['groups'][number] = { triggers: [] };
    const name = g.name.trim();
    if (!name) out.name = 'Enter a group name.';
    else if (name.length > 100) out.name = 'At most 100 characters.';
    if (!GROUP_ID.test(g.id) || g.id === EVERYONE_ELSE) out.id = `The path id “${g.id}” is not valid: lower-case letters, digits, - and _.`;
    else if (c.groups.filter((x) => x.id === g.id).length > 1) out.id = `Two groups share the path id “${g.id}”.`;
    const checked = validateActionBased({ triggers: g.triggers, delay: { kind: 'none' }, cancelOn: [], reevaluateAudienceAtSend: false });
    out.triggers = checked.issues.filter((i) => i.path[0] === 'triggers');
    return out;
  });
  return { fields, groups };
}

export const hasIssues = (i: ActionPathsIssues) => Object.keys(i.fields).length > 0 || i.groups.some((g) => g.name || g.id || g.triggers.length);
